import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { Box, Grid, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import CustomTextField from "../../common/CustomTextField";
import CustomNumberField from "../../common/CustomNumberField";
import CustomCheckbox from "../../common/CustomCheckBox";
import { FieldLabel } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";
import {
  formatSalesConfigTargetRange,
  getSalesConfigTargetRangeEditorValues,
  SalesConfigTargetRateFormRow,
  validateCompPercent,
  validateSalesConfigTargetRangeBounds,
  validateSalesConfigTargetRangeOverlap,
  validateTargetPercentBound,
} from "./isrTypes";

export interface SalesConfigTargetRateEditorPanelHandle {
  /** Validates the form and returns values, or null if invalid. */
  commit: () => Promise<SalesConfigTargetRateFormRow | null>;
}

interface SalesConfigTargetRateEditorPanelProps {
  mode: "add" | "edit";
  row?: SalesConfigTargetRateFormRow | null;
  existingRanges: string[];
  excludeIndex?: number | null;
  onCancel: () => void;
}

// CS-2144 START
type TargetRateEditorFormValues = {
  targetPercentFrom: string;
  targetPercentTo: string;
  noUpperLimit: boolean;
  targetCompPercent: string;
  active: boolean;
};

const emptyTargetRateEditorValues = (): TargetRateEditorFormValues => ({
  targetPercentFrom: "",
  targetPercentTo: "",
  noUpperLimit: false,
  targetCompPercent: "",
  active: true,
});

// CS-2144 progress bar hidden
// const RANGE_TRACK_MAX = 100;
// const RANGE_TRACK_HEIGHT = 12;
// const RANGE_TRACK_MARKS = [0, 25, 50, 75, 100];
// const clampTrack = (value: number) => Math.min(Math.max(value, 0), RANGE_TRACK_MAX);
// const trackPercent = (value: number) => `${(clampTrack(value) / RANGE_TRACK_MAX) * 100}%`;
// const roundTrack = (value: number) => Math.round(value);
// const trackReadout = (from: number, to: number, open: boolean) =>
//   open ? `${roundTrack(from)}% and above` : `${roundTrack(from)}% to ${roundTrack(to)}%`;
// CS-2144 END

const SalesConfigTargetRateEditorPanel = forwardRef<
  SalesConfigTargetRateEditorPanelHandle,
  SalesConfigTargetRateEditorPanelProps
>(({
  mode,
  row,
  existingRanges,
  excludeIndex = null,
  onCancel,
}, ref) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { tokens, inputFieldSx, cancelBtnSx } = useModalStyles();
  const {
    control,
    reset,
    trigger,
    getValues,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<TargetRateEditorFormValues>({
    defaultValues: emptyTargetRateEditorValues(),
  });
  const isEdit = mode === "edit";
  const noUpperLimit = watch("noUpperLimit");
  const fromValue = watch("targetPercentFrom");
  const toValue = watch("targetPercentTo");
  const fromNum = Number(fromValue);
  const toNum = Number(toValue);
  const hasFrom = String(fromValue).trim() !== "" && !Number.isNaN(fromNum);
  const hasTo = String(toValue).trim() !== "" && !Number.isNaN(toNum);
  // CS-2144 progress bar hidden
  // const sliderFrom = hasFrom ? clampTrack(fromNum) : 0;
  // const sliderTo = noUpperLimit ? RANGE_TRACK_MAX : hasTo ? clampTrack(toNum) : sliderFrom;
  // const usedBands = existingRanges.flatMap((range, index) => {
  //   if (excludeIndex != null && index === excludeIndex) {
  //     return [];
  //   }
  //   const bounds = parseSalesConfigTargetRange(range);
  //   return bounds ? [{ range, ...bounds }] : [];
  // });
  const boundsMessage = hasFrom && (noUpperLimit || hasTo)
    ? validateSalesConfigTargetRangeBounds(fromValue, toValue, noUpperLimit)
    : true;
  const overlapMessage = boundsMessage === true && hasFrom && (noUpperLimit || hasTo)
    ? validateSalesConfigTargetRangeOverlap(
      formatSalesConfigTargetRange(fromValue, toValue, noUpperLimit),
      existingRanges,
      excludeIndex
    )
    : true;
  const rangeHint = overlapMessage !== true ? overlapMessage : "";
  // CS-2144 progress bar hidden
  // const readout = !hasFrom
  //   ? "Set the range"
  //   : noUpperLimit
  //     ? `${fromNum}% and above`
  //     : hasTo
  //       ? `${fromNum}% to ${toNum}%`
  //       : `${fromNum}% to …`;

  useImperativeHandle(ref, () => ({
    commit: async () => {
      const valid = await trigger();
      if (!valid) {
        return null;
      }
      const values = getValues();
      return {
        targetRange: formatSalesConfigTargetRange(
          values.targetPercentFrom,
          values.targetPercentTo,
          values.noUpperLimit
        ),
        targetCompPercent: values.targetCompPercent,
        active: values.active,
      };
    },
  }), [trigger, getValues]);

  useEffect(() => {
    if (isEdit && row) {
      const rangeValues = getSalesConfigTargetRangeEditorValues(row.targetRange);
      reset({
        ...rangeValues,
        targetCompPercent: row.targetCompPercent,
        active: row.active !== false,
      });
      return;
    }
    reset(emptyTargetRateEditorValues());
  }, [mode, row, isEdit, reset]);

  useEffect(() => {
    if (!hasFrom) {
      if (noUpperLimit) {
        setValue("noUpperLimit", false);
      }
      return;
    }
    if (noUpperLimit) {
      setValue("targetPercentTo", "");
      clearErrors("targetPercentTo");
    }
  }, [hasFrom, noUpperLimit, setValue, clearErrors]);

  useEffect(() => {
    const toMessage = errors.targetPercentTo?.message;
    if (overlapMessage === true && typeof toMessage === "string" && toMessage.includes("overlaps")) {
      clearErrors("targetPercentTo");
    }
  }, [overlapMessage, errors.targetPercentTo, clearErrors]);

  // CS-2144 START
  const validateToField = () => {
    const values = getValues();
    if (values.noUpperLimit) {
      if (validateTargetPercentBound(values.targetPercentFrom, "From %", { allowZero: true }) !== true) {
        return true;
      }
      return validateSalesConfigTargetRangeOverlap(
        formatSalesConfigTargetRange(values.targetPercentFrom, values.targetPercentTo, true),
        existingRanges,
        excludeIndex
      );
    }
    const toValidation = validateTargetPercentBound(values.targetPercentTo, "To %", { allowZero: true });
    if (toValidation !== true) {
      return toValidation;
    }
    if (validateTargetPercentBound(values.targetPercentFrom, "From %", { allowZero: true }) !== true) {
      return true;
    }
    if (Number(values.targetPercentTo) <= Number(values.targetPercentFrom)) {
      return "To % must be greater than From %.";
    }
    return validateSalesConfigTargetRangeOverlap(
      formatSalesConfigTargetRange(values.targetPercentFrom, values.targetPercentTo, false),
      existingRanges,
      excludeIndex
    );
  };
  // CS-2144 END

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.border}`,
        borderRadius: "10px",
        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.cardBg,
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography fontWeight={700} sx={{ fontSize: "0.9rem", color: tokens.textPrimary }}>
          {isEdit ? "Edit Target Rate" : "Add Target Rate"}
        </Typography>
        <IconButton type="button" onClick={onCancel} size="small" aria-label="Close form" sx={{ color: tokens.textSecondary, p: 0.35 }}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Grid container spacing={1.5} alignItems="flex-start">
        {/* CS-2144 START */}
        {/* CS-2144 progress bar and the percent line above it are hidden.
        <Grid item xs={12}>
          <Typography ref={readoutRef} fontWeight={700} sx={{ fontSize: "1.05rem", color: tokens.textPrimary }} aria-live="polite">
            {readout}
          </Typography>
          <Box sx={{ position: "relative", mt: 1, mx: 1 }}>
            <Box
              sx={{
                pointerEvents: "none",
                position: "absolute",
                left: 0,
                right: 0,
                top: "50%",
                height: RANGE_TRACK_HEIGHT,
                transform: "translateY(-50%)",
                borderRadius: 99,
                boxSizing: "border-box",
                border: `1px solid ${tokens.inputBorder}`,
                backgroundColor: tokens.bg,
              }}
            >
              {usedBands.map((band) => (
                <Box
                  key={band.range}
                  title={`${band.range} already used`}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: trackPercent(band.from),
                    right: `calc(100% - ${trackPercent(band.to)})`,
                    borderRadius: 99,
                    background: `repeating-linear-gradient(45deg, ${tokens.inputBorder} 0 4px, ${tokens.inputBg} 4px 8px)`,
                  }}
                />
              ))}
            </Box>
            <Slider
              key={`${sliderFrom}-${sliderTo}-${noUpperLimit}`}
              defaultValue={[sliderFrom, sliderTo]}
              min={0}
              max={RANGE_TRACK_MAX}
              step={1}
              disableSwap
              getAriaLabel={(index) => (index === 0 ? "From handle" : "To handle")}
              onChange={(_event, value) => {
                const [nextFrom, nextTo] = value as number[];
                if (readoutRef.current) {
                  readoutRef.current.textContent = trackReadout(nextFrom, nextTo, noUpperLimit);
                }
              }}
              onChangeCommitted={(_event, value) => {
                const [nextFrom, nextTo] = value as number[];
                setValue("targetPercentFrom", String(roundTrack(nextFrom)), { shouldDirty: true });
                if (!noUpperLimit) {
                  setValue("targetPercentTo", String(roundTrack(nextTo)), { shouldDirty: true });
                }
              }}
              sx={{
                display: "block",
                color: tokens.accent,
                height: RANGE_TRACK_HEIGHT,
                padding: "16px 0",
                "& .MuiSlider-rail": { opacity: 0 },
                "& .MuiSlider-track": {
                  height: RANGE_TRACK_HEIGHT,
                  border: "none",
                  backgroundColor: tokens.accent,
                  transition: "none",
                },
                "& .MuiSlider-thumb": {
                  width: 20,
                  height: 20,
                  backgroundColor: tokens.bg,
                  border: `3px solid ${tokens.accent}`,
                  transition: "none",
                  transform: "translate(-50%, -50%)",
                },
                "& .MuiSlider-thumb.Mui-focusVisible, & .MuiSlider-thumb:hover": {
                  boxShadow: "none",
                },
                "& .MuiSlider-thumb.Mui-disabled": { borderColor: tokens.textSecondary },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", color: tokens.textSecondary, fontSize: "0.7rem" }}>
            {RANGE_TRACK_MARKS.map((mark) => (
              <span key={mark}>{mark}%</span>
            ))}
          </Box>
        </Grid>
        */}
        {rangeHint && (
          <Grid item xs={12}>
            <Typography sx={{ color: tokens.error, fontSize: "0.75rem" }}>{rangeHint}</Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <FieldLabel label="From (%)" required tokens={tokens} />
          <Box sx={inputFieldSx(!!errors.targetPercentFrom)}>
            <CustomNumberField
              control={control}
              controllerName="targetPercentFrom"
              label=""
              size="small"
              allowDecimal
              decimalScale={2}
              inputAdornment="%"
              adornmentPosition="end"
              inputProps={{ "aria-label": "From (%)" }}
              rules={{
                validate: (value: string) => validateTargetPercentBound(value, "From %", { allowZero: true }),
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FieldLabel label="To (%)" required={!noUpperLimit} tokens={tokens} />
          <Box sx={hasFrom ? {
            ...inputFieldSx(!!errors.targetPercentTo),
            "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
              ...(inputFieldSx(!!errors.targetPercentTo)["& .MuiOutlinedInput-root, & .MuiInputBase-root"]),
              borderRadius: "10px 10px 0 0 !important",
            },
          } : inputFieldSx(!!errors.targetPercentTo)}>
            <CustomNumberField
              control={control}
              controllerName="targetPercentTo"
              label=""
              size="small"
              allowDecimal
              decimalScale={2}
              inputAdornment="%"
              adornmentPosition="end"
              isDisabled={noUpperLimit}
              inputProps={{ "aria-label": "To (%)" }}
              rules={{ validate: validateToField }}
            />
          </Box>
          {hasFrom && (
            <Box
              sx={{
                mt: "-1px",
                border: `1px solid ${tokens.inputBorder}`,
                borderTop: "none",
                borderRadius: "0 0 10px 10px",
                backgroundColor: tokens.inputBg,
                px: 0.75,
                "& .MuiFormControlLabel-root": { m: 0, alignItems: "center" },
                "& .MuiCheckbox-root": { p: 0.25 },
                "& .MuiSvgIcon-root": { fontSize: "0.95rem" },
                "& .MuiTypography-root": { fontSize: "0.72rem", lineHeight: 1.2, marginTop: "0 !important" },
              }}
            >
              <CustomCheckbox
                control={control}
                controllerName="noUpperLimit"
                label={`Anything above ${fromNum}%`}
                tooltipMessage="Saves this range with no upper limit"
              />
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <FieldLabel label="Comp %" required tokens={tokens} />
          <Box sx={inputFieldSx(!!errors.targetCompPercent)}>
            <CustomTextField
              control={control}
              controllerName="targetCompPercent"
              label=""
              size="small"
              rules={{ validate: (value: string) => validateCompPercent(value, "Comp %") }}
            />
          </Box>
        </Grid>
        {/* CS-2144 END */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
            <CustomCheckbox control={control} controllerName="active" label="Active" />
            <Box
              component="button"
              type="button"
              onClick={onCancel}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 0.85,
                fontSize: "0.8rem",
                border: "1px solid",
                cursor: "pointer",
              }}
            >
              Close
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

SalesConfigTargetRateEditorPanel.displayName = "SalesConfigTargetRateEditorPanel";

export default SalesConfigTargetRateEditorPanel;
